import { formatTokenText, TOKEN_RE } from '@/lib/templateVariables'
import { cleanEmailHtml } from '@/lib/emailEditorConfig'

/**
 * Le due direzioni di conversione dei token di personalizzazione:
 * `{{key | 'fallback'}}` nell'HTML salvato ↔ `<span data-token-key>` nell'editor.
 */

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
}

/** Il fallback è testo libero interpolato in una stringa HTML: un `"` digitato
 * a mano nella sintassi del token troncherebbe l'attributo che lo trasporta. */
function escapeHtml(text: string): string {
  return text.replace(/[&<>"]/g, (char) => HTML_ESCAPES[char])
}

function unescapeHtml(text: string): string {
  return text
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
}

/**
 * Convert stored HTML (with {{key}} text tokens) to editor HTML (with
 * <span data-token-key> nodes). This lets the editor display chips for
 * tokens that were saved as plain text.
 */
export function tokenTextToNodes(html: string): string {
  return html.replace(
    TOKEN_RE,
    (_, key, fallback) => {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
      const fb = fallback ? ` data-token-fallback="${escapeHtml(fallback)}"` : ''
      const display = fallback ? `${label} | '${escapeHtml(fallback)}'` : label
      return `<span data-token-key="${key}" data-token-label="${label}"${fb} class="personalization-token">${display}</span>`
    },
  )
}

/**
 * Convert editor HTML (with <span data-token-key> nodes) back to stored
 * HTML with {{key}} or {{key | 'fallback'}} text tokens.
 */
export function tokenNodesToText(html: string): string {
  return cleanEmailHtml(
    html.replace(
      /<span[^>]*data-token-key="(\w+)"[^>]*>[^<]*<\/span>/g,
      (span, key) => {
        // L'ordine degli attributi non è garantito: il fallback si rilegge dal
        // match invece di pretendere una posizione fissa nel pattern.
        const fallback = span.match(/data-token-fallback="([^"]*)"/)?.[1]
        return formatTokenText(key, fallback === undefined ? null : unescapeHtml(fallback))
      },
    ),
  )
}
